import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogForm,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Plus, Trash2, Copy } from "lucide-react";
import { trpc } from "@/trpc/trpc";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/operators")({
  component: OperatorsPage,
});

const addSchema = z.object({
  email: z.string().email("Podaj poprawny email"),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
});

type AddFormData = z.infer<typeof addSchema>;

function OperatorsPage() {
  const { data: operators, isLoading } = trpc.salon.listOperators.useQuery();
  const utils = trpc.useUtils();
  const [dialogOpen, setDialogOpen] = useState(false);

  const addOperator = trpc.salon.addOperator.useMutation({
    onSuccess: () => {
      utils.salon.listOperators.invalidate();
      utils.salon.getOperatorStats.invalidate();
      setDialogOpen(false);
      toast.success("Operator dodany");
    },
    onError: (err) => toast.error(err.message),
  });

  const removeOperator = trpc.salon.removeOperator.useMutation({
    onSuccess: () => {
      utils.salon.listOperators.invalidate();
      utils.salon.getOperatorStats.invalidate();
      toast.success("Operator usunięty");
    },
    onError: (err) => toast.error(err.message),
  });

  const form = useForm<AddFormData>({
    resolver: zodResolver(addSchema),
    defaultValues: { email: "", firstName: "", lastName: "" },
  });

  const onSubmit = form.handleSubmit((data) => {
    addOperator.mutate({
      email: data.email,
      firstName: data.firstName || null,
      lastName: data.lastName || null,
    });
  });

  function copyCode(code: string) {
    navigator.clipboard.writeText(code);
    toast.success("Kod skopiowany");
  }

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Ładowanie...</p>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Operatorzy</h1>
          <p className="text-sm text-muted-foreground">
            {operators?.length ?? 0} operatorów
          </p>
        </div>
        <Button size="sm" onClick={() => { form.reset(); setDialogOpen(true); }}>
          <Plus className="mr-1 size-4" />
          Dodaj operatora
        </Button>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="px-4 py-3 font-medium">Operator</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Kod</th>
                <th className="px-4 py-3 font-medium">Łącznie</th>
                <th className="px-4 py-3 font-medium">Poprz. miesiąc</th>
                <th className="px-4 py-3 font-medium">Ten miesiąc</th>
                <th className="px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {operators?.map((op) => {
                const name =
                  [op.firstName, op.lastName].filter(Boolean).join(" ") || "—";
                return (
                  <tr key={op.userId} className="border-b last:border-0">
                    <td className="px-4 py-3 font-medium">{name}</td>
                    <td className="px-4 py-3 text-muted-foreground">{op.email}</td>
                    <td className="px-4 py-3">
                      {op.code ? (
                        <button
                          onClick={() => copyCode(op.code!)}
                          className="inline-flex items-center gap-1 rounded bg-muted px-2 py-1 font-mono text-xs hover:bg-muted/80"
                        >
                          {op.code}
                          <Copy className="size-3" />
                        </button>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 font-semibold">{op.totalInstalls}</td>
                    <td className="px-4 py-3 text-muted-foreground">{op.previousMonthInstalls}</td>
                    <td className="px-4 py-3 font-semibold">{op.monthlyInstalls}</td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (confirm("Usunąć operatora?")) {
                            removeOperator.mutate({ userId: op.userId });
                          }
                        }}
                      >
                        <Trash2 className="size-4 text-destructive" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
              {operators?.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                    Brak operatorów — dodaj pierwszego operatora
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Dodaj operatora</DialogTitle>
          </DialogHeader>
          <DialogForm onSubmit={onSubmit}>
            <DialogBody className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="op-email">Email</Label>
                <Input
                  id="op-email"
                  type="email"
                  placeholder="operator@example.com"
                  {...form.register("email")}
                />
                {form.formState.errors.email && (
                  <p className="text-sm text-destructive">
                    {form.formState.errors.email.message}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="op-first">Imię (opcjonalnie)</Label>
                  <Input id="op-first" {...form.register("firstName")} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="op-last">Nazwisko (opcjonalnie)</Label>
                  <Input id="op-last" {...form.register("lastName")} />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Operator otrzyma automatycznie wygenerowany kod do udostępniania klientom.
                Każda instalacja aplikacji z tym kodem będzie zliczana.
              </p>
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>
                Anuluj
              </Button>
              <Button type="submit" disabled={addOperator.isPending}>
                {addOperator.isPending ? "Dodawanie..." : "Dodaj operatora"}
              </Button>
            </DialogFooter>
          </DialogForm>
        </DialogContent>
      </Dialog>
    </div>
  );
}
